/**
 * The opening: this table, being prepared, in about seven and a half seconds.
 *
 * The first version of this was a small diorama floating in the middle of a
 * blank green overlay - its own little felt, its own little tiles, its own
 * geometry - and then the real table cut in underneath it. A human reviewer
 * looked at the running game and said there was no convincing opening, and
 * they were right: nothing that happened on that diorama happened to the table
 * you were about to play on.
 *
 * So the ceremony now runs *inside the table*. The scene is a child of `.pit`,
 * which means it inherits the plane's tilt and perspective and reads the same
 * `--wall-stack`, `--wall-depth` and `--ring` the real wall measures itself
 * with. A ceremony tile's resting place is the pixel its wall stack occupies.
 * That single fact is what makes the last beat work: the ceremony wall does not
 * fade out and get replaced, it fades *into* the real wall standing in exactly
 * the same place.
 *
 * Six beats, each physically different from the ones on either side:
 *
 *   settle   the felt comes up and the table is empty
 *   shuffle  136 tiles churn face-down across the middle, each on its own
 *            orbit, at its own speed, with its own lift off the felt
 *   wall     the churn resolves side by side into four walls of seventeen
 *            stacks, and the stacks visibly gain their second tile
 *   dice     two six-sided dice tumble in, bounce, and settle face-up
 *   deal     packets leave the wall and travel to the four seats, three rounds
 *            of four and then one single each, while the real racks and the
 *            real hand fill in underneath
 *   ready    the ceremony hands the table over
 *
 * Nothing here is a frame loop. The whole sequence is one state value that
 * changes six times; every tile, die and packet animates from CSS keyed off
 * `data-stage`. React renders six times in seven seconds.
 *
 * It is also pure presentation. No seed, no wall order, no tile face and no
 * hand reaches this file: scatter, churn and dice faces come from `spread()`,
 * a hash of an element's own index, so the ceremony is identical on every
 * client and tells you nothing about the game behind it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useI18n } from "../i18n";
import type { OpeningPreference } from "../game/openingPreference";
import { playCue } from "../game/tableAudio";

export type CeremonyStage = "settle" | "shuffle" | "wall" | "dice" | "deal" | "ready";
export type CeremonyState = CeremonyStage | "done";

export const CEREMONY_STAGES: readonly CeremonyStage[] = [
  "settle",
  "shuffle",
  "wall",
  "dice",
  "deal",
  "ready",
];

const STAGE_KEYS = {
  settle: "opening.stage.settle",
  shuffle: "opening.stage.shuffle",
  wall: "opening.stage.wall",
  dice: "opening.stage.dice",
  deal: "opening.stage.deal",
  ready: "opening.stage.ready",
} as const;

type Timeline = readonly (readonly [number, CeremonyState])[];

/**
 * Full runs 7.7s.
 *
 * The shuffle gets the longest beat - a shade over two seconds - because it is
 * the only stage with nothing to read and everything to watch, and because a
 * churn that is over before you have focused on it is just a flicker. The wall
 * build is second: it is the moment disorder becomes order, and it has to be
 * slow enough that you can see one side finish before the next one does.
 */
const FULL_TIMELINE: Timeline = [
  [0, "settle"],
  [550, "shuffle"],
  [2_700, "wall"],
  [4_300, "dice"],
  [5_400, "deal"],
  [7_000, "ready"],
  [7_700, "done"],
];

/** Fast keeps every beat at roughly a quarter of the length: the shape, no wait. */
const FAST_TIMELINE: Timeline = [
  [0, "settle"],
  [140, "shuffle"],
  [680, "wall"],
  [1_080, "dice"],
  [1_360, "deal"],
  [1_760, "ready"],
  [1_960, "done"],
];

/**
 * Reduced motion gets the same six beats and the same final table, quickly and
 * without the travel: the CSS drops every orbit, tumble and flight, so what is
 * left is the table being set one still frame at a time. A player who has asked
 * the system for less motion should not be made to watch 136 tiles fly.
 */
const REDUCED_TIMELINE: Timeline = [
  [0, "settle"],
  [260, "shuffle"],
  [900, "wall"],
  [1_500, "dice"],
  [2_000, "deal"],
  [2_500, "ready"],
  [2_900, "done"],
];

/** Four walls of seventeen stacks: 68 elements, 136 tiles. */
const SIDES = ["north", "east", "south", "west"] as const;
const PER_SIDE = 17;
const TILE_COUNT = SIDES.length * PER_SIDE;

/**
 * A spread of small integers from one index.
 *
 * Every scattered position, churn offset, timing jitter and die face in the
 * ceremony comes from here. It is a hash, not a random number generator: the
 * same tile is always thrown to the same place, so the opening is identical on
 * every client and in every screenshot, and nothing it produces has any
 * relationship to the deal.
 */
function spread(index: number, salt: number, range: number): number {
  // A multiply-and-take-the-remainder is not enough here: consecutive indices
  // step by a constant, and the first attempt at this laid all 68 tiles out
  // along one diagonal instead of in a pile. The avalanche below is what makes
  // neighbouring tiles land nowhere near each other.
  let mixed = (index * 2_654_435_761 + salt * 40_503) >>> 0;
  mixed ^= mixed >>> 16;
  mixed = Math.imul(mixed, 2_246_822_507) >>> 0;
  mixed ^= mixed >>> 13;
  mixed = Math.imul(mixed, 3_266_489_909) >>> 0;
  mixed ^= mixed >>> 16;
  // XOR-assign hands back a *signed* 32-bit integer, so the last shift has to
  // be undone before the remainder or half the tiles get thrown off the table.
  return (mixed >>> 0) % range;
}

/** The same hash over a string, for the one thing that varies per round. */
function hashKey(key: string): number {
  let hash = 2_166_136_261;
  for (let at = 0; at < key.length; at += 1) {
    hash = Math.imul(hash ^ key.charCodeAt(at), 16_777_619);
  }
  return hash >>> 0;
}

/**
 * One stack, at rest and in motion.
 *
 * `--i` and the side put it in its wall; everything else is where it lies and
 * how it moves before it gets there. Positions are fractions of `--ring` rather
 * than pixels, so the same numbers work on a 1440px table and a 390px one.
 */
function ceremonyTile(index: number): CSSProperties {
  const sideIndex = Math.floor(index / PER_SIDE);
  const slot = index % PER_SIDE;
  return {
    "--i": slot,
    // Piled across the middle of the ring, well inside the walls.
    "--sx": (spread(index, 11, 1_000) / 1_000 - 0.5) * 0.82,
    "--sy": (spread(index, 29, 1_000) / 1_000 - 0.5) * 0.7,
    "--sr": spread(index, 47, 360) - 180,
    // Its own orbit: no two tiles in the pile ever move together.
    "--cx": (spread(index, 71, 1_000) / 1_000 - 0.5) * 0.16,
    "--cy": (spread(index, 97, 1_000) / 1_000 - 0.5) * 0.13,
    "--cr": spread(index, 113, 120) - 60,
    "--cz": spread(index, 149, 7),
    "--cdelay": `${spread(index, 131, 760)}ms`,
    // A pile of tiles is not one colour. A few percent either way catches the
    // lamp differently on each back, which is the difference between a heap of
    // tiles and a sheet of green rectangles.
    "--tone": 0.9 + spread(index, 191, 22) / 100,
    "--cdur": `${520 + spread(index, 173, 9) * 45}ms`,
    // When it flies home. Sides land one after another, and each wall builds
    // from one end, so you watch order arrive rather than find it already there.
    "--wdelay": `${sideIndex * 210 + slot * 26}ms`,
    // How far into the deal this stack is taken off the wall, for the quarter
    // of the ring that visibly empties while the packets are going out.
    "--ddelay": `${slot * 30}ms`,
  } as CSSProperties;
}

/** Pip layout for a die face, as occupied cells of a 3x3 grid. */
const DIE_PIPS: Record<number, readonly number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

/** Opposite faces of a die sum to seven. Given the face that lands up, the
 *  other five follow, which is what makes the tumble look like one object. */
function cubeFaces(up: number): Record<string, number> {
  const down = 7 - up;
  const rest = [1, 2, 3, 4, 5, 6].filter((value) => value !== up && value !== down);
  const front = rest[0] ?? 1;
  const back = 7 - front;
  const right = rest.find((value) => value !== front && value !== back) ?? 2;
  return { up, down, front, back, right, left: 7 - right };
}

/**
 * Only the pips that are actually on.
 *
 * Twelve faces are in the DOM at once - two dice, six sides each - so drawing
 * nine cells per face and hiding the blanks put a hundred spare elements on
 * every table in the app. Placing each pip on the 3x3 grid by hand costs one
 * style and saves two thirds of them.
 */
function Pips({ face }: { face: number }) {
  return (
    <>
      {(DIE_PIPS[face] ?? []).map((cell) => (
        <span
          key={cell}
          className="opening__pip"
          style={{ gridArea: `${Math.floor(cell / 3) + 1} / ${(cell % 3) + 1}` }}
        />
      ))}
    </>
  );
}

/**
 * A real six-sided cube, not a square with dots on it.
 *
 * The container is a flat element lying on the tilted plane, so its own +Z is
 * straight up out of the table - which means the face that should land upwards
 * is simply the unrotated one, and the tumble can end with the rolled value
 * readable.
 *
 * Three layers, because each one has to move differently. The outer span flies
 * the arc and carries the fade; the cube inside it only spins, so the tumble
 * turns about the die's own centre wherever the arc has put it; and the shadow
 * is pushed back down onto the felt by the inverse of the arc's height, so it
 * stays on the table while the die is in the air. Splitting them is also what
 * stops the contact shadow tumbling along with the cube.
 *
 * `data-value` rides on every face because a one and a four are red on a real
 * set - the one cue that tells dice from ivory tiles at a glance.
 */
function Die({ value, index }: { value: number; index: number }) {
  const faces = cubeFaces(value);
  return (
    <span className="opening__die" style={{ "--die": index } as CSSProperties} aria-hidden="true">
      <span className="opening__die-shadow" />
      <span className="opening__cube">
        {(["up", "down", "front", "back", "right", "left"] as const).map((face) => (
          <span key={face} className="opening__face" data-face={face} data-value={faces[face] ?? 1}>
            <Pips face={faces[face] ?? 1} />
          </span>
        ))}
      </span>
    </span>
  );
}

function useReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => globalThis.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = globalThis.matchMedia?.(query);
    if (!media) return;
    const update = (): void => setReduced(media.matches);
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

export interface Ceremony {
  stage: CeremonyState;
  /** The two values the dice land on. Decoration; the engine has no dice. */
  dice: readonly [number, number];
  skip: () => void;
}

/**
 * The ceremony's clock.
 *
 * Lifted out of the view because the ceremony is no longer one box: the scene
 * belongs on the tilted plane inside the pit and the caption belongs flat over
 * the table, and the table itself needs to know the stage so the real wall,
 * racks and hand can appear on the right beat. One hook, two views, one
 * `data-opening` attribute.
 *
 * `ceremonyKey` is the round. Tutorial passes nothing and gets nothing.
 */
export function useOpeningCeremony(
  ceremonyKey: string | number | undefined,
  preference: OpeningPreference,
): Ceremony {
  const reduced = useReducedMotion();
  const inactive = ceremonyKey === undefined || preference === "off";
  const [stage, setStage] = useState<CeremonyState>(inactive ? "done" : "settle");

  useEffect(() => {
    if (inactive) {
      setStage("done");
      return;
    }
    setStage("settle");
    const timeline = reduced ? REDUCED_TIMELINE : preference === "fast" ? FAST_TIMELINE : FULL_TIMELINE;
    const timers = timeline
      .slice(1)
      .map(([delay, next]) => globalThis.setTimeout(() => setStage(next), delay));
    return () => timers.forEach((timer) => globalThis.clearTimeout(timer));
  }, [ceremonyKey, preference, reduced, inactive]);

  // The two beats that make a noise at a real table. Tied to the stage rather
  // than to a timer, so Fast, Skip and Off are all correct for free: a stage
  // that is never entered is never heard.
  useEffect(() => {
    if (stage === "shuffle") playCue("shuffle");
    if (stage === "dice") playCue("dice");
  }, [stage]);

  const dice = useMemo<readonly [number, number]>(() => {
    const hash = hashKey(String(ceremonyKey ?? ""));
    return [(hash % 6) + 1, (Math.floor(hash / 6) % 6) + 1];
  }, [ceremonyKey]);

  const skip = useCallback(() => setStage("done"), []);

  return { stage, dice, skip };
}

/**
 * The physical half: tiles, walls, dice and packets, on the table's own plane.
 *
 * Mounted inside `.pit`, so `--ring`, `--wall-stack` and `--wall-depth` are the
 * real wall's, and a stack's resting transform is the real stack's position.
 */
export function OpeningScene({ stage, dice }: { stage: CeremonyState; dice: readonly [number, number] }) {
  if (stage === "done") return null;

  return (
    <div className="opening-scene" data-stage={stage} data-testid="opening-scene" aria-hidden="true">
      <div className="opening__ring">
        <div className="opening__tiles">
          {Array.from({ length: TILE_COUNT }, (_, index) => (
            <span
              key={index}
              className="opening__tile"
              data-side={SIDES[Math.floor(index / PER_SIDE)]}
              style={ceremonyTile(index)}
            />
          ))}
        </div>

        <div className="opening__dice">
          <Die value={dice[0]} index={0} />
          <Die value={dice[1]} index={1} />
        </div>

        {/*
          * The deal, as a circuit rather than a burst: three rounds of four
          * tiles going round the table and then one single each, which is
          * exactly how thirteen tiles reach a hand. A round leaves the wall
          * while the one before it is still landing.
          */}
        <div className="opening__deal">
          {Array.from({ length: 16 }, (_, index) => {
            const round = Math.floor(index / 4);
            const seat = index % 4;
            return (
              <span
                key={index}
                className="opening__packet"
                data-seat={SIDES[seat]}
                data-single={round === 3}
                style={{ "--delay": `${round * 300 + seat * 68}ms` } as CSSProperties}
              >
                {Array.from({ length: round === 3 ? 1 : 4 }, (_, tile) => (
                  <span key={tile} className="opening__packet-tile" style={{ "--n": tile } as CSSProperties} />
                ))}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export interface OpeningChromeProps {
  stage: CeremonyState;
  dice: readonly [number, number];
  dealerName: string;
  turnName: string;
  onSkip: () => void;
}

/**
 * The flat half: what beat we are on, and the way out of it.
 *
 * Deliberately small and low - a line on the rail rather than a title card -
 * because the thing worth looking at is the table, and a ceremony that has to
 * caption itself in 24px has already failed.
 */
export function OpeningChrome({ stage, dice, dealerName, turnName, onSkip }: OpeningChromeProps) {
  const { t } = useI18n();
  if (stage === "done") return null;
  const index = CEREMONY_STAGES.indexOf(stage);

  return (
    <div
      className="opening"
      data-testid="opening-ceremony"
      data-stage={stage}
      aria-label={t("opening.title")}
    >
      <button type="button" className="button button--ghost opening__skip" onClick={onSkip}>
        {t("opening.skip")}
      </button>

      <div className="opening__caption">
        <p className="opening__beats" aria-hidden="true">
          {CEREMONY_STAGES.map((beat, at) => (
            <span key={beat} className="opening__beat" data-state={at <= index ? "done" : "todo"} />
          ))}
        </p>
        <p className="opening__line" role="status" aria-live="polite">
          <strong>{t(STAGE_KEYS[stage])}</strong>
          <span>
            {stage === "ready"
              ? t("opening.readyDetail", { dealer: dealerName, player: turnName })
              : stage === "dice"
                ? t("opening.detail.dice", { a: String(dice[0]), b: String(dice[1]) })
                : t(`opening.detail.${stage}`)}
          </span>
        </p>
      </div>
    </div>
  );
}
